import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ITeam, ITeamsIdMap } from "@gtmhub/teams";
import { CommentsScrollToEditorEvent } from "@webapp/comments";
import { LocalizePipe } from "@webapp/localization/pipes/localize.pipe";
import { CheckInTemplate } from "@webapp/reflections/models/checkins-models";
import { ReflectionPanel } from "@webapp/reflections/models/reflection-panel.models";
import {
  ReflectionAnswerChange,
  ReflectionMoodChange,
  ReflectionNoteChange,
  ReflectionOrder,
  ReflectionQuestion,
} from "@webapp/reflections/models/reflection-question.model";
import {
  CheckinCommentChange,
  CheckinCommentPendingTextChange,
  CheckinCommentsToggle,
  DEFAULT_CADENCE_DURATION_IN_DAYS,
  ReflectionMoodType,
  ReflectionNote,
  ReflectionOverviewFilterName,
} from "@webapp/reflections/models/reflections.models";
import {
  buildCheckinCommentsCounterAriaLabel,
  generateCommentTemplate,
  getLastMondayAsDate,
  getLatestDateFromComments,
  isNoteFilterEnabled,
} from "@webapp/reflections/utils";
import { isReflectionEmpty } from "@webapp/reflections/utils/is-reflection-empty";
import dayjs from "@webapp/shared/libs/dayjs";
import { OkrLinkClickEvent } from "@webapp/shared/rich-text-editor/events/okr-link.event";

@Component({
  selector: "reflection-compare",
  templateUrl: "./reflection-compare.component.html",
  styleUrls: ["./reflection-compare.component.less"],
  providers: [LocalizePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionCompareComponent implements OnInit, OnChanges, AfterContentChecked {
  @Input() public currReflection?: ReflectionPanel;
  @Input() public prevReflection?: ReflectionPanel;
  @Input() public filter: ReflectionOverviewFilterName;
  @Input() public currCadenceStartDate: string;
  @Input() public prevCadenceStartDate: string;
  @Input() public checkInTemplate: CheckInTemplate;
  @Input() public scrollToCommentsEditor$: BehaviorSubject<CommentsScrollToEditorEvent> = new BehaviorSubject<CommentsScrollToEditorEvent>(null);
  @Input() public team: ITeam;
  @Input() public teams: ITeamsIdMap;

  @Output() public readonly moodUpdate = new EventEmitter<ReflectionMoodChange>();
  @Output() public readonly noteUpdate = new EventEmitter<ReflectionNoteChange>();
  @Output() public readonly answerUpdate = new EventEmitter<ReflectionAnswerChange>();
  @Output() public readonly fillOut = new EventEmitter<ReflectionOrder>();
  @Output() public readonly edit = new EventEmitter<ReflectionOrder>();
  @Output() public readonly cancelEdit = new EventEmitter<ReflectionOrder>();
  @Output() public readonly publish = new EventEmitter<ReflectionOrder>();
  @Output() public readonly commentsToggle = new EventEmitter<CheckinCommentsToggle>();
  @Output() public readonly commentAdd = new EventEmitter<CheckinCommentChange>();
  @Output() public readonly commentRemove = new EventEmitter<CheckinCommentChange>();
  @Output() public readonly commentPendingTextChange = new EventEmitter<CheckinCommentPendingTextChange>();
  @Output() public readonly okrLinkClick = new EventEmitter<OkrLinkClickEvent>();

  public isReflectionEmpty = isReflectionEmpty;
  public isCurrentCadence: boolean;
  public currQuestionsFormatted: ReflectionQuestion[];
  public prevQuestionsFormatted: ReflectionQuestion[];
  public generateCommentTemplate = generateCommentTemplate;
  public getLatestDateFromComments = getLatestDateFromComments;
  public isNoteFilterEnabled = isNoteFilterEnabled;
  public buildCheckinCommentsCounterAriaLabel = buildCheckinCommentsCounterAriaLabel;

  constructor(private cdr: ChangeDetectorRef, private localizePipe: LocalizePipe, private elementRef: ElementRef) {}

  public ngOnInit(): void {
    this.currQuestionsFormatted = this.formatCurrQuestions();
    this.prevQuestionsFormatted = structuredClone(this.formatPrevQuestions());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.isCurrentCadence = dayjs(this.currCadenceStartDate).isSame(getLastMondayAsDate());
    if (changes.currReflection && !changes.currReflection.isFirstChange()) {
      this.currQuestionsFormatted = this.formatCurrQuestions();
    }
    if (changes.prevReflection && !changes.prevReflection.isFirstChange()) {
      this.prevQuestionsFormatted = structuredClone(this.formatPrevQuestions());
    }
  }

  public ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  public onMoodChange(mood: ReflectionMoodType, type: ReflectionOrder): void {
    this.moodUpdate.emit({ type, mood });
  }

  public onNoteChange(change: ReflectionNote, type: ReflectionOrder, index: number): void {
    this.noteUpdate.emit({ type, note: change, noteIndex: index });
  }

  public onAnswerChange(question: ReflectionQuestion, type: ReflectionOrder): void {
    this.answerUpdate.emit({ type, question });
  }

  public onEditReflection(type: ReflectionOrder): void {
    this.edit.emit(type);
  }

  public onCancelReflection(type: ReflectionOrder): void {
    this.cancelEdit.emit(type);
    this.cdr.detectChanges();
  }

  public onPublishReflection(type: ReflectionOrder): void {
    this.publish.emit(type);
  }

  public onFillOutReflection(type: ReflectionOrder): void {
    this.fillOut.emit(type);
  }

  public onToggleComments(data: CheckinCommentsToggle): void {
    this.commentsToggle.emit(data);

    // Fix scroll position when toggle comments section
    if (data.source !== "addButton") {
      window.setTimeoutOutsideAngular(() => {
        const targetElement = this.elementRef.nativeElement.querySelector(`[id='${data.targetId}']`);

        if (targetElement) {
          targetElement.scrollIntoView({ block: "center" });
        }
      });
    }
  }

  public onAddComment(data: CheckinCommentChange): void {
    this.commentAdd.emit(data);
  }

  public onRemoveComment(data: CheckinCommentChange): void {
    this.commentRemove.emit(data);
  }

  public onChangeCommentPendingText(data: CheckinCommentPendingTextChange): void {
    this.commentPendingTextChange.emit(data);
  }

  public shouldRenderMood(checkIn: ReflectionPanel): boolean {
    return checkIn?.settings?.moodsEnabled || (!checkIn?.id && this.checkInTemplate.moodsEnabled);
  }

  public shouldRenderNote(checkIn: ReflectionPanel, note: ReflectionNote): boolean {
    return (checkIn?.settings?.notesEnabled || (!checkIn?.id && this.checkInTemplate.notesEnabled)) && (checkIn?.isEditing || !!note?.text || !!note?.gif?.id);
  }

  public shouldRenderQuestion(checkIn: ReflectionPanel, question: ReflectionQuestion): boolean {
    return (question?.answer || checkIn.isEditing || question?.gif?.id) && question?.isEnabled;
  }

  public shouldRenderCurrentNoCheckIn(): boolean {
    return (!this.currReflection || (this.currReflection.noReflection && (!this.isCurrentCadence || !this.currReflection.isCurrentUser))) && !this.filter;
  }

  public shouldRenderPreviousNoCheckIn(): boolean {
    return (!this.prevReflection || this.prevReflection.noReflection) && !this.filter;
  }

  private formatCurrQuestions(): ReflectionQuestion[] {
    const currQuestionsFormatted = structuredClone(this.currReflection?.questions) || [];

    this.prevReflection?.questions?.forEach((prevQuestion) => {
      const isQuestionMatched = currQuestionsFormatted.some((currQuestion) => currQuestion?.id === prevQuestion?.id);
      if (!isQuestionMatched) {
        currQuestionsFormatted.push(undefined);
      }
    });

    return currQuestionsFormatted;
  }

  // Calculate cadence methods are used only for tracking purposes
  public calculateCurrCadenceStartDate(): string {
    return dayjs(this.currCadenceStartDate).utc().add(1, "day").toISOString();
  }

  public calculateCurrCadenceEndDate(): string {
    return dayjs(this.currCadenceStartDate).utc().add(DEFAULT_CADENCE_DURATION_IN_DAYS, "day").toISOString();
  }

  public calculatePrevCadenceStartDate(): string {
    return dayjs(this.prevCadenceStartDate).utc().add(1, "day").toISOString();
  }

  public calculatePrevCadenceEndDate(): string {
    return dayjs(this.currCadenceStartDate).utc().toISOString();
  }

  public formatCadenceDates(cadenceStartDate: string): string {
    const formattedStartDate = dayjs(cadenceStartDate).format("MMM D");
    const formattedEndDate = dayjs(cadenceStartDate).utc().add(DEFAULT_CADENCE_DURATION_IN_DAYS, "day").format("MMM D");

    return this.localizePipe.transform("reflections_cadence_label", { dateRange: `${formattedStartDate} - ${formattedEndDate}` });
  }

  private formatPrevQuestions(): ReflectionQuestion[] {
    const prevQuestions: ReflectionQuestion[] = [];
    const currQuestions = this.currReflection?.questions || [];

    currQuestions.forEach((currQuestion) => {
      prevQuestions.push(this.prevReflection?.questions?.find((prevQuestion) => prevQuestion?.id === currQuestion?.id));
    });

    this.prevReflection?.questions?.forEach((prevQuestion) => {
      const isQuestionMatched = currQuestions.some((currQuestion) => currQuestion?.id === prevQuestion?.id);
      if (!isQuestionMatched) {
        prevQuestions.push(prevQuestion);
      }
    });

    return prevQuestions;
  }
}
